// src/actions/taxonomy_actions.js
// Categories and collections against the commerce engine.
//
// Plain async helpers rather than redux thunks, following order_edit_actions.js: these
// screens own small, self-contained state and refetch after every write. Putting them
// in the commerce_admin reducer would give two components a claim on the same keys for
// no benefit.
//
// Every helper returns { ok, code?, message?, data? } so a caller never has to know
// which of the two failure shapes it got — a thrown transport error, or a 200 carrying
// { errors: [...] } in the payload.
import { commerceGraphql } from "./commerce_admin_actions";
import { classifyFailure } from "./engineVersionSkew";

async function run(query, variables, key) {
  try {
    const data = await commerceGraphql(query, variables);
    const payload = data && data[key];
    const errs = (payload && payload.errors) || [];
    if (errs.length) return { ok: false, code: errs[0].code || "ERROR", field: errs[0].field, message: errs[0].message || "Save failed" };
    return { ok: true, data: payload };
  } catch (e) {
    return { ok: false, code: "TRANSPORT", message: e.message };
  }
}

const RESULT = "ok id errors{ code field message }";

// ---- reads ----

// Staff sees UNPUBLISHED collections too — the engine keys that off ctx.staff, which
// the shared commerceGraphql already sends the token for. Nothing here asks for drafts
// explicitly; asking would imply a non-staff caller could.
export async function fetchTaxonomy() {
  try {
    const d = await commerceGraphql(
      `{ categories{ id slug name description parent level productCount }
         collections{ id slug name description isPublished productCount } }`, {});
    return { ok: true, categories: d.categories || [], collections: d.collections || [] };
  } catch (e) {
    // An engine older than v51 has no taxonomy at all. Saying so beats surfacing a raw
    // "Cannot query field" to an operator who has no way to read that as a version skew.
    return { ...classifyFailure(e), categories: [], collections: [] };
  }
}

// A collection's members, in curated order. Asks for the same product shape the
// storefront gets, so what the operator reorders is what a visitor would see.
export async function fetchCollection(slug) {
  try {
    const d = await commerceGraphql(
      `query($s:String!){ collection(slug:$s){ id slug name description isPublished productCount
         products{ id slug name thumbnail{ url } } } }`, { s: slug });
    return { ok: true, collection: d.collection || null };
  } catch (e) {
    return { ok: false, message: e.message, collection: null };
  }
}

// The product's current category, read SEPARATELY from the main product query and
// deliberately so.
//
// AdminProduct.category does not exist before engine v51, and GraphQL rejects the WHOLE
// query on an unknown field — so selecting it inside the shared ADMIN_PRODUCT_FIELDS
// took down /orders/products and every product detail page the moment the dashboard
// deployed ahead of the engine. That is not hypothetical: it happened on 2026-08-14,
// because this dashboard auto-deploys on a main merge and the engine needs a manual
// build. The two will never be in lockstep, so a page must not hard-fail on a field the
// engine may not have yet.
//
// `unsupported` is returned rather than thrown, so the picker can say why it is
// unavailable instead of showing an error or, worse, an empty selection that looks like
// "no category" and invites an operator to overwrite a real one.
export async function fetchProductCategory(productId) {
  try {
    const d = await commerceGraphql(`{ adminProducts(first: 200){ items{ id category } } }`, {});
    const row = (((d.adminProducts || {}).items) || []).find((p) => String(p.id) === String(productId));
    return { ok: true, category: row ? row.category : null };
  } catch (e) {
    // The engine predates the field. Any other failure is a real error.
    return classifyFailure(e);
  }
}

// The pool to pick members from. Deliberately the ADMIN product list, not
// products(channel:) — the latter is now channel-scoped and would hide a product the
// operator legitimately wants to feature but has not yet listed in that channel.
export async function fetchProductPool() {
  try {
    const d = await commerceGraphql(`{ adminProducts(first: 200){ items{ id slug name isPublished } } }`, {});
    return { ok: true, products: ((d.adminProducts || {}).items) || [] };
  } catch (e) {
    return { ok: false, message: e.message, products: [] };
  }
}

// ---- writes ----

export const saveCategory = (input) =>
  run(`mutation($i:AdminCategoryInput!){ adminCategoryUpsert(input:$i){ ${RESULT} } }`, { i: input }, "adminCategoryUpsert");

export const deleteCategory = (id) =>
  run(`mutation($id:Int!){ adminCategoryDelete(id:$id){ ok id orphanedProducts errors{ code field message } } }`,
    { id }, "adminCategoryDelete");

export const saveCollection = (input) =>
  run(`mutation($i:AdminCollectionInput!){ adminCollectionUpsert(input:$i){ ${RESULT} } }`, { i: input }, "adminCollectionUpsert");

export const deleteCollection = (id) =>
  run(`mutation($id:Int!){ adminCollectionDelete(id:$id){ ${RESULT} } }`, { id }, "adminCollectionDelete");

// The FULL membership list, in order. The engine replaces rather than merges, so
// sending a partial list silently removes everything absent from it — callers must
// send what they want the collection to be, never a delta.
export const setCollectionProducts = (collectionId, productIds) =>
  run(`mutation($i:AdminCollectionProductsInput!){ adminCollectionProductsSet(input:$i){ ${RESULT} } }`,
    { i: { collectionId, productIds } }, "adminCollectionProductsSet");

// null clears the category.
export const setProductCategory = (productId, category) =>
  run(`mutation($i:AdminProductCategoryInput!){ adminProductCategorySet(input:$i){ ${RESULT} } }`,
    { i: { productId, category: category || null } }, "adminProductCategorySet");
